import { Report, ObjectCount, ReportParams, EventReportExportParams, EventReportExportResponse } from "../../models/reports";
import {
  AuditReportCatalogEntry,
  AuditReportExportResponse,
  AuditReportParams,
  AuditReportTable,
} from "../../models/auditReports";
import { API_ENDPOINTS } from "../../utils/api-service";
import { api } from "./api";

const unwrapData = <T>(response: unknown, fallback: T): T => {
  if (typeof response === "object" && response !== null) {
    const record = response as Record<string, unknown>;
    if (record.success === true && record.data !== undefined) {
      return record.data as T;
    }
  }

  return (response as T) ?? fallback;
};

const auditReportParams = (params: AuditReportParams): Record<string, string> => ({
  report: params.report,
  grain: params.grain,
  tenantId: params.tenantId,
  dateFrom: params.dateFrom,
  dateTo: params.dateTo,
});


export const reportsApi = api.injectEndpoints({
  endpoints: (build) => ({
     getUsersCount: build.query<ObjectCount, void>({
      query: () => ({
        url: "/reports/usersCount",
      }),
      transformResponse: (response: any) => {
        if (!response) return { count: 0 };
        return response.success ? response.data : response;
      },
    }),
     getUniqueCustomersCount: build.query<ObjectCount, void>({
      query: () => ({
        url: "/reports/customers/unique",
      }),
      transformResponse: (response: any) => {
        if (!response) return { count: 0 };
        return response.success ? response.data : response;
      },
    }),
     getRepeatingCustomersCount: build.query<ObjectCount, void>({
      query: () => ({
        url: "/reports/customers/repeating",
      }),
      transformResponse: (response: any) => {
        if (!response) return { count: 0 };
        return response.success ? response.data : response;
      },
      }),
      getMetricsData: build.query<Report[], ReportParams>({
       query: (params) => ({
         url: `/reports/metrics/${params.id}`,
         params: params
       }),
       transformResponse: (response: any) => {
         if (!response || response === "" || response === "{}") return [];
         if (response.success && Array.isArray(response.data)) {
           return response.data;
         }
         return Array.isArray(response) ? response : [];
       },
       }),
       exportEventsReport: build.mutation<EventReportExportResponse, EventReportExportParams>({
        query: (params) => ({
          url: API_ENDPOINTS.REPORTS.EVENTS_EXPORT,
          params: {
            ...(params.tenantId ? { tenantId: params.tenantId } : {}),
            dateFrom: params.dateFrom,
            dateTo: params.dateTo,
            sortBy: params.sortBy,
          },
          responseHandler: async (response) => {
            if (response.status === 204) {
              return { status: 204 as const };
            }

            if (!response.ok) {
              const contentType = response.headers.get("Content-Type") || "";
              if (contentType.includes("json")) {
                const body = (await response.json()) as {
                  error?: { message?: string };
                  message?: string;
                };
                const message =
                  body.error?.message ||
                  body.message ||
                  `Error al exportar (${response.status})`;
                throw new Error(message);
              }

              const message = await response.text();
              throw new Error(message || `Error al exportar (${response.status})`);
            }

            const contentType = response.headers.get("Content-Type") || "";
            const isSpreadsheet =
              contentType.includes("text/csv") ||
              contentType.includes("spreadsheet") ||
              contentType.includes("excel") ||
              contentType.includes("octet-stream");

            if (!isSpreadsheet) {
              const message = await response.text();
              throw new Error(
                message || `Tipo de contenido inesperado: ${contentType}`
              );
            }

            const blob = await response.blob();
            const disposition = response.headers.get("Content-Disposition") || "";
            const filenameMatch = disposition.match(/filename="?([^";]+)"?/);
            const filename = filenameMatch?.[1] ?? "events-report.csv";

            return { status: 200 as const, blob, filename };
          },
        }),
       }),
       getAuditReportCatalog: build.query<AuditReportCatalogEntry[], void>({
        query: () => ({
          url: API_ENDPOINTS.REPORTS.AUDIT_CATALOG,
        }),
        transformResponse: (response: unknown) =>
          unwrapData<AuditReportCatalogEntry[]>(response, []),
        providesTags: [{ type: "reports", id: "AUDIT_CATALOG" }],
       }),
       // Un solo endpoint para todos los reportes de auditoria: el reporte es un
       // parametro, no una ruta. La respuesta trae las columnas, asi que la
       // tabla del BO no necesita saber que columnas tiene cada reporte.
       getAuditReport: build.query<AuditReportTable, AuditReportParams>({
        query: (params) => ({
          url: API_ENDPOINTS.REPORTS.AUDIT,
          params: auditReportParams(params),
        }),
        transformResponse: (response: unknown) =>
          unwrapData<AuditReportTable | null>(response, null) as AuditReportTable,
        keepUnusedDataFor: 300,
        providesTags: (_result, _error, params) => [
          { type: "reports", id: `AUDIT_${params.report}_${params.grain}` },
        ],
       }),
       exportAuditReport: build.mutation<AuditReportExportResponse, AuditReportParams>({
        query: (params) => ({
          url: API_ENDPOINTS.REPORTS.AUDIT_EXPORT,
          params: auditReportParams(params),
          responseHandler: async (response) => {
            if (response.status === 204) {
              return { status: 204 as const };
            }

            if (!response.ok) {
              const contentType = response.headers.get("Content-Type") || "";
              if (contentType.includes("json")) {
                const body = (await response.json()) as {
                  error?: { message?: string };
                  message?: string;
                };
                const message =
                  body.error?.message ||
                  body.message ||
                  `Error al exportar (${response.status})`;
                throw new Error(message);
              }

              const message = await response.text();
              throw new Error(message || `Error al exportar (${response.status})`);
            }

            const contentType = response.headers.get("Content-Type") || "";
            if (!contentType.includes("csv") && !contentType.includes("octet-stream")) {
              const message = await response.text();
              throw new Error(message || `Tipo de contenido inesperado: ${contentType}`);
            }

            const blob = await response.blob();
            const disposition = response.headers.get("Content-Disposition") || "";
            const filenameMatch = disposition.match(/filename="?([^";]+)"?/);
            const filename = filenameMatch?.[1] ?? "reporte-auditoria.csv";

            return { status: 200 as const, blob, filename };
          },
        }),
       }),
    }),
});

export const {
  useGetUsersCountQuery,
  useGetUniqueCustomersCountQuery,
  useGetRepeatingCustomersCountQuery,
  useGetMetricsDataQuery,
  useExportEventsReportMutation,
  useGetAuditReportCatalogQuery,
  useGetAuditReportQuery,
  useExportAuditReportMutation,
} = reportsApi;
